import { useState, useEffect } from 'react';
import { r2BucketCreate } from '../../api/r2/bucketCreate';
import { r2BucketDelete } from '../../api/r2/bucketDelete';
import { r2BucketList } from '../../api/r2/bucketList';
import type { AppError } from '../../api/errors';
import { toAppError } from '../../api/errors';
import { vscodeTheme } from '../../styles/theme';
import { ApiErrorNotice } from '../shared/ApiErrorNotice';
import { SearchableSelect } from '../shared/SearchableSelect';
import { useToast } from '../shared/Toast';

interface BucketSelectorProps {
  selectedBucket: string | null;
  onBucketSelect: (bucketId: string | null) => void;
}

export function BucketSelector({ selectedBucket, onBucketSelect }: BucketSelectorProps) {
  const { showToast } = useToast();
  const [buckets, setBuckets] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AppError | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string> | null>(null);
  const [filterText, setFilterText] = useState('');
  const [creating, setCreating] = useState(false);
  const [deletingBucket, setDeletingBucket] = useState<string | null>(null);

  useEffect(() => {
    loadBuckets();
  }, []);

  const handleError = (apiError: AppError) => {
    setError(apiError);
    setFieldErrors(apiError.error.fields ?? null);
    if (apiError.error.details) {
      console.error('Bucket selector error details:', apiError.error.details);
    }
  };

  const clearError = () => {
    setError(null);
    setFieldErrors(null);
  };

  const loadBuckets = async (): Promise<string[]> => {
    setLoading(true);
    clearError();

    try {
      const result = await r2BucketList();
      setBuckets(result);
      // Auto-select first bucket if none selected
      if (!selectedBucket && result.length > 0) {
        onBucketSelect(result[0]);
      }
      setLoading(false);
      return result;
    } catch (error) {
      handleError(toAppError(error));
      setLoading(false);
      return [];
    }
  };

  const handleCreateBucket = async () => {
    const trimmed = filterText.trim();
    if (!trimmed) {
      return;
    }
    setCreating(true);
    clearError();

    try {
      const result = await r2BucketCreate({ bucket_id: trimmed });
      await loadBuckets();
      onBucketSelect(result.name);
      setFilterText('');
      setCreating(false);
    } catch (error) {
      handleError(toAppError(error));
      setCreating(false);
    }
  };

  const handleDeleteBucket = async (bucketName: string) => {
    if (deletingBucket) {
      return;
    }
    const confirmed = window.confirm(`バケット "${bucketName}" を削除しますか？`);
    if (!confirmed) {
      return;
    }
    setDeletingBucket(bucketName);
    clearError();

    try {
      const result = await r2BucketDelete({ bucket_id: bucketName });
      showToast(`バケット "${result.name}" を削除しました`, 'success');
      const updatedBuckets = await loadBuckets();
      if (bucketName === selectedBucket) {
        onBucketSelect(updatedBuckets[0] ?? null);
      }
      setDeletingBucket(null);
    } catch (error) {
      const appError = toAppError(error);
      handleError(appError);
      if (appError.status === 409) {
        window.alert('バケット内のファイルを先に削除してください');
      } else {
        showToast(appError.message, 'error');
      }
      setDeletingBucket(null);
    }
  };

  const canCreateBucket =
    filterText.trim().length > 0 &&
    !buckets.some((bucket) => bucket.toLowerCase() === filterText.trim().toLowerCase());

  if (loading) {
    return (
      <div style={{ color: vscodeTheme.colors.textPrimary, fontSize: '13px' }}>
        Loading...
      </div>
    );
  }

  return (
    <div>
      <ApiErrorNotice error={error} />
      {fieldErrors?.bucket_id ? (
        <div style={{ color: '#f48771', fontSize: '12px', marginBottom: '6px' }}>
          {fieldErrors.bucket_id}
        </div>
      ) : null}
      <SearchableSelect
        label="BUCKET"
        items={buckets.map((bucket) => ({ id: bucket, label: bucket }))}
        selectedId={selectedBucket}
        onChange={(bucketId) => onBucketSelect(bucketId)}
        placeholder={selectedBucket ?? 'Filter...'}
        filterText={filterText}
        onFilterTextChange={setFilterText}
        emptyText="No buckets found"
        renderItemActions={(item) => {
          const isDeleting = deletingBucket === item.id;
          return (
            <span
              role="button"
              aria-label={`Delete bucket ${item.label}`}
              onClick={(e) => {
                e.stopPropagation();
                if (!isDeleting) {
                  void handleDeleteBucket(item.id);
                }
              }}
              onMouseEnter={(e) => {
                if (!isDeleting) {
                  e.currentTarget.style.color = vscodeTheme.colors.error;
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = vscodeTheme.colors.textSecondary;
              }}
              style={{
                padding: '4px',
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                color: vscodeTheme.colors.textSecondary,
                opacity: isDeleting ? 0.5 : 1,
              }}
            >
              ×
            </span>
          );
        }}
        renderFooter={
          canCreateBucket
            ? () => (
                <div
                  onClick={creating ? undefined : handleCreateBucket}
                  style={{
                    padding: '6px 8px',
                    cursor: creating ? 'not-allowed' : 'pointer',
                    borderTop: `1px solid ${vscodeTheme.colors.border}`,
                    color: vscodeTheme.colors.textPrimary,
                    fontSize: vscodeTheme.typography.fontSize,
                    fontFamily: vscodeTheme.typography.fontFamily,
                    backgroundColor: creating
                      ? vscodeTheme.colors.editorBackground
                      : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!creating) {
                      e.currentTarget.style.backgroundColor =
                        vscodeTheme.colors.hoverBackground;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!creating) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {creating
                    ? `Creating "${filterText.trim()}"...`
                    : `+ New Bucket "${filterText.trim()}"`}
                </div>
              )
            : undefined
        }
      />
    </div>
  );
}
